import { cartSlice } from 'cart'
import { Translate } from 'common'
import { observer } from 'mobx-react-lite'
import { ReactElement, useState } from 'react'
import { useSelector } from 'react-redux'
import { Vehicle } from 'src/store/models/Vehicles'
import { useUiStateStore } from 'src/store/uiState/UiStateStore'
import { useUserStore } from 'src/store/user/UserStore'
import { defaultTheme } from 'src/theme'
import { breakpoints } from 'src/theme/breakpoints'
import { Checkbox } from 'src/ui-components'
import { ICheckboxClickHandler } from 'src/ui-components/forms/Checkbox'
import { RootState, store } from 'store'
import styled from 'styled-components'
import { getVehicleOrderFormData } from '../store/cartSelectors'
import { OrderFormTextInput } from './OrderFormTextInput'

interface SellerCartOrderFormProps {
  currentVehicle: Vehicle
}

export const SellerCartOrderForm = observer(
  ({ currentVehicle }: SellerCartOrderFormProps): ReactElement => {
    const userStore = useUserStore()
    const { isMultisellerUser } = useUiStateStore()

    const poRequired = userStore?.preferences?.cart_poRequired === 'true'
    const poMaxSize = userStore?.preferences?.cart_poMaxSize

    const { poNumber, customerName, personalNote } = useSelector(
      (state: RootState) => getVehicleOrderFormData(state, currentVehicle)
    )

    const isSamePoNumber = !!poNumber

    const [isSharedPoChecked, setIsSharedPoChecked] = useState(isSamePoNumber)

    const sellers = useSelector(
      (state: RootState) =>
        state.cart.cartData.vehicles.find(
          (v) => v.vehicle.id === currentVehicle.id
        )?.sellers ?? []
    )

    // const showError = showVehicleOrderFormError(currentVehicle)
    const showError = false //TODO , Value is set before placing order, by validating order request payload data

    const propagatePoNumberToAllSellers = (value: string) => {
      store.dispatch(
        cartSlice.actions.setFieldOnVehicleCart({
          field: 'poNumber',
          vehicle: currentVehicle,
          value,
        })
      )
      sellers.forEach((seller) =>
        store.dispatch(
          cartSlice.actions.setFieldOnSeller({
            field: 'poNumber',
            vehicle: currentVehicle,
            seller,
            value,
          })
        )
      )
    }

    return (
      <MainContainer>
        {isMultisellerUser() && (
          <Checkbox
            onClick={(data: ICheckboxClickHandler) => {
              setIsSharedPoChecked(data.isChecked)
              propagatePoNumberToAllSellers(data.isChecked ? poNumber : '')
            }}
            active={isSharedPoChecked}
            value="samePoNumber"
            label={Translate('poNumberIsTheSameForAllSellers')}
          />
        )}

        <FormContainer>
          {isMultisellerUser() && (
            <OrderFormTextInput
              width="228px"
              label="poNumber"
              onChange={(value) => propagatePoNumberToAllSellers(value)}
              value={poNumber}
              disabled={!isSharedPoChecked}
              noMargin
              required={poRequired}
              showMaxSize
              maxSize={Number(poMaxSize)}
              showError={showError}
            />
          )}
          <OrderFormTextInput
            width="228px"
            label="customerName"
            onChange={(value) =>
              store.dispatch(
                cartSlice.actions.setFieldOnVehicleCart({
                  field: 'customerName',
                  vehicle: currentVehicle,
                  value,
                })
              )
            }
            value={customerName}
            showSubLabel
            noMargin={!isMultisellerUser()}
          />
          <OrderFormTextInput
            width="395px"
            label="personalNote"
            onChange={(value) =>
              store.dispatch(
                cartSlice.actions.setFieldOnVehicleCart({
                  field: 'personalNote',
                  vehicle: currentVehicle,
                  value,
                })
              )
            }
            value={personalNote}
            showSubLabel
            noMargin
          />
        </FormContainer>
      </MainContainer>
    )
  }
)

const MainContainer = styled.div`
  margin: 35px;

  @media (max-width: ${breakpoints.tablet}px) {
    margin: 16px;
  }
`

const FormContainer = styled.div`
  width: 100%;
  max-width: 100%;
  background-color: ${defaultTheme.colors.componentsBackground};
  overflow-x: hidden;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 12px;
  @media (max-width: ${breakpoints.tablet}px) {
    flex-direction: column;
    padding: 5px 0;
  }
  margin-top: 40px;
`
